import React from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  Stack,
  Typography
} from '@mui/material'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import PropTypes from 'prop-types'
import { StyledBox } from '../components/styledComponents/StyledItemComponents'
import { theme } from '../theme'

const MAP_URL = 'https://maps.google.com/?q=47.039722,21.955236'
const SHOWROOM_IMAGE = '/primusvest-contact-showroom.jpeg'

const infoCards = [
  {
    icon: <PlaceOutlinedIcon />,
    title: 'Adresa punct de lucru',
    lines: [
      'Str. Seleusului 117',
      'Oradea, jud. Bihor',
      'Cod postal 410508'
    ],
    action: {
      label: 'Deschide in Google Maps',
      href: MAP_URL
    }
  },
  {
    icon: <PhoneOutlinedIcon />,
    title: 'Telefon',
    lines: [
      '0259 266 094',
      '0787 731 831'
    ]
  },
  {
    icon: <EmailOutlinedIcon />,
    title: 'Email',
    lines: [
      'office@primusvest.ro'
    ],
    action: {
      label: 'Trimite email',
      href: 'mailto:office@primusvest.ro'
    }
  },
  {
    icon: <BusinessOutlinedIcon />,
    title: 'Date firma',
    lines: [
      'PRIMUSVEST SRL',
      'CUI RO35750706',
      'Nr. Reg. Com. J05/485/2016'
    ]
  }
]

const ContactInfoCard = ({ card }) => (
  <Card
    elevation={0}
    sx={{
      height: '100%',
      border: '1px solid rgba(49,49,49,0.12)',
      borderRadius: '18px',
      backgroundColor: '#fff',
      boxShadow: '0 12px 36px rgba(29,29,27,0.06)'
    }}
  >
    <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
      <Stack spacing={2}>
        <Box
          sx={{
            width: 46,
            height: 46,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '14px',
            backgroundColor: 'rgba(226,6,19,0.09)',
            color: theme.palette.accent.main
          }}
        >
          {card.icon}
        </Box>
        <Box>
          <Typography variant='h6' sx={{ fontSize: '17px', fontWeight: 800, mb: 1 }}>
            {card.title}
          </Typography>
          <Stack spacing={0.4}>
            {card.lines.map((line) => (
              <Typography key={line} variant='body1' sx={{ color: theme.palette.fontColor.main, lineHeight: 1.55 }}>
                {line}
              </Typography>
            ))}
          </Stack>
        </Box>
        {card.action && (
          <Button
            component='a'
            href={card.action.href}
            target={card.action.href.startsWith('http') ? '_blank' : undefined}
            rel={card.action.href.startsWith('http') ? 'noopener noreferrer' : undefined}
            endIcon={card.action.href.startsWith('http') ? <OpenInNewOutlinedIcon /> : null}
            sx={{
              width: 'fit-content',
              px: 0,
              color: theme.palette.accent.main,
              fontWeight: 800,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'transparent',
                color: theme.palette.fontColor.dark
              }
            }}
          >
            {card.action.label}
          </Button>
        )}
      </Stack>
    </CardContent>
  </Card>
)

ContactInfoCard.propTypes = {
  card: PropTypes.shape({
    icon: PropTypes.node.isRequired,
    title: PropTypes.string.isRequired,
    lines: PropTypes.arrayOf(PropTypes.string).isRequired,
    action: PropTypes.shape({
      label: PropTypes.string.isRequired,
      href: PropTypes.string.isRequired
    })
  }).isRequired
}

const Contact = () => {
  return (
    <Box sx={{ backgroundColor: theme.palette.secondary.main, minHeight: '70vh', py: { xs: 3, md: 5 } }}>
      <StyledBox sx={{ px: { xs: 2, md: 5 } }}>
        <Box
          sx={{
            overflow: 'hidden',
            borderRadius: { xs: '20px', md: '28px' },
            backgroundColor: theme.palette.primary.main,
            border: '1px solid rgba(49,49,49,0.12)',
            boxShadow: '0 18px 60px rgba(29,29,27,0.08)'
          }}
        >
          <Grid container>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  minHeight: { xs: 300, md: 560 },
                  height: '100%',
                  backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.02), rgba(0,0,0,0.18)), url(${SHOWROOM_IMAGE})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
                role='img'
                aria-label='Primusvest Oradea showroom'
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack spacing={3.5} sx={{ p: { xs: 3, md: 5 } }}>
                <Box>
                  <Typography
                    variant='overline'
                    sx={{
                      color: theme.palette.accent.main,
                      fontWeight: 900,
                      letterSpacing: '0.16em'
                    }}
                  >
                    Contact Primusvest
                  </Typography>
                  <Typography
                    variant='h1'
                    sx={{
                      mt: 1,
                      fontSize: { xs: '34px', md: '48px' },
                      lineHeight: 1.05,
                      fontWeight: 900,
                      color: theme.palette.fontColor.dark
                    }}
                  >
                    Scule si utilaje profesionale in Oradea
                  </Typography>
                  <Typography variant='body1' sx={{ mt: 2, color: theme.palette.fontColor.medium, lineHeight: 1.8 }}>
                    Pentru oferte, comenzi si informatii comerciale, ne poti contacta direct sau poti ajunge la punctul nostru de lucru din Oradea.
                  </Typography>
                </Box>
                <Divider />
                <Stack spacing={1.25}>
                  <Typography variant='body1' sx={{ fontWeight: 800 }}>
                    Program si disponibilitate
                  </Typography>
                  <Typography variant='body2' sx={{ color: theme.palette.fontColor.medium, lineHeight: 1.7 }}>
                    Contacteaza echipa Primusvest pentru confirmarea disponibilitatii produselor, ridicare, livrare sau detalii despre comenzile plasate online.
                  </Typography>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <Button
                    component='a'
                    href={MAP_URL}
                    target='_blank'
                    rel='noopener noreferrer'
                    variant='contained'
                    endIcon={<OpenInNewOutlinedIcon />}
                    sx={{
                      backgroundColor: theme.palette.accent.main,
                      color: '#fff',
                      borderRadius: '999px',
                      px: 2.5,
                      py: 1.2,
                      fontWeight: 900,
                      textTransform: 'none',
                      boxShadow: '0 10px 24px rgba(226,6,19,0.28)',
                      '&:hover': { backgroundColor: '#b9000d' }
                    }}
                  >
                    Vezi locatia
                  </Button>
                  <Button
                    component='a'
                    href='tel:0259266094'
                    variant='outlined'
                    sx={{
                      borderColor: 'rgba(49,49,49,0.24)',
                      color: theme.palette.fontColor.dark,
                      borderRadius: '999px',
                      px: 2.5,
                      py: 1.2,
                      fontWeight: 900,
                      textTransform: 'none',
                      '&:hover': {
                        borderColor: theme.palette.accent.main,
                        backgroundColor: 'rgba(226,6,19,0.05)'
                      }
                    }}
                  >
                    Suna acum
                  </Button>
                </Stack>
              </Stack>
            </Grid>
          </Grid>
        </Box>

        <Grid container spacing={2.5} sx={{ mt: 3 }}>
          {infoCards.map((card) => (
            <Grid item xs={12} sm={6} lg={3} key={card.title}>
              <ContactInfoCard card={card} />
            </Grid>
          ))}
        </Grid>

        <Box
          sx={{
            mt: 3,
            overflow: 'hidden',
            borderRadius: '22px',
            border: '1px solid rgba(49,49,49,0.12)',
            backgroundColor: '#fff',
            boxShadow: '0 12px 36px rgba(29,29,27,0.06)'
          }}
        >
          <Box
            component='a'
            href={MAP_URL}
            target='_blank'
            rel='noopener noreferrer'
            sx={{
              minHeight: { xs: 260, md: 340 },
              p: { xs: 3, md: 5 },
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              gap: 4,
              color: 'inherit',
              textDecoration: 'none',
              background:
                'radial-gradient(circle at 85% 20%, rgba(226,6,19,0.16), transparent 28%), linear-gradient(135deg, #ffffff 0%, #f7f7f7 44%, #eeeeee 100%)'
            }}
          >
            <Stack spacing={2} sx={{ maxWidth: 560 }}>
              <Box
                sx={{
                  width: 54,
                  height: 54,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '16px',
                  backgroundColor: theme.palette.accent.main,
                  color: '#fff',
                  boxShadow: '0 10px 24px rgba(226,6,19,0.28)'
                }}
              >
                <PlaceOutlinedIcon />
              </Box>
              <Box>
                <Typography variant='h5' sx={{ fontSize: { xs: '24px', md: '32px' }, fontWeight: 900, mb: 1 }}>
                  Gaseste-ne pe harta
                </Typography>
                <Typography variant='body1' sx={{ color: theme.palette.fontColor.medium, lineHeight: 1.8 }}>
                  Punctul de lucru Primusvest este pe Str. Seleusului 117, Oradea, jud. Bihor, cod postal 410508.
                </Typography>
              </Box>
              <Typography sx={{ color: theme.palette.accent.main, fontWeight: 900 }}>
                Deschide locatia in Google Maps
              </Typography>
            </Stack>
            <Box
              sx={{
                width: { xs: '100%', md: 360 },
                minHeight: 180,
                borderRadius: '20px',
                border: '1px solid rgba(49,49,49,0.14)',
                backgroundColor: '#fff',
                position: 'relative',
                overflow: 'hidden',
                boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.8)'
              }}
            >
              <Box sx={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(90deg, rgba(49,49,49,0.08) 1px, transparent 1px), linear-gradient(0deg, rgba(49,49,49,0.08) 1px, transparent 1px)', backgroundSize: '42px 42px' }} />
              <Box sx={{ position: 'absolute', left: '14%', right: '8%', top: '52%', height: '12px', borderRadius: '999px', backgroundColor: 'rgba(226,6,19,0.18)', transform: 'rotate(-8deg)' }} />
              <Box sx={{ position: 'absolute', left: '38%', right: '18%', top: '27%', height: '12px', borderRadius: '999px', backgroundColor: 'rgba(49,49,49,0.13)', transform: 'rotate(26deg)' }} />
              <Box sx={{ position: 'absolute', left: '62%', right: '4%', top: '68%', height: '12px', borderRadius: '999px', backgroundColor: 'rgba(49,49,49,0.1)', transform: 'rotate(14deg)' }} />
              <Box
                sx={{
                  position: 'absolute',
                  left: '50%',
                  top: '48%',
                  width: 62,
                  height: 62,
                  transform: 'translate(-50%, -50%)',
                  borderRadius: '50% 50% 50% 0',
                  rotate: '-45deg',
                  backgroundColor: theme.palette.accent.main,
                  boxShadow: '0 14px 28px rgba(226,6,19,0.28)'
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  left: '50%',
                  top: '48%',
                  width: 22,
                  height: 22,
                  transform: 'translate(-50%, -50%)',
                  borderRadius: '50%',
                  backgroundColor: '#fff'
                }}
              />
              <Typography sx={{ position: 'absolute', left: 20, bottom: 16, fontWeight: 900, color: theme.palette.fontColor.dark }}>
                47.039722, 21.955236
              </Typography>
            </Box>
          </Box>
        </Box>
      </StyledBox>
    </Box>
  )
}

export default Contact
